import {LocationContext, RouteResult, SiteEntity, useLazyGetRoutingOriginManyResultQuery} from "@ots-frontend/ots-data";
import {
  findSiteConnections,
  type LiveMapDisplayOptions,
  routeKey,
  type SiteConnection,
  SITE_ROAD_ROUTE_LIMIT,
  type SiteRoadRoute
} from "@ots-frontend/ots-ui-tw";
import {useEffect, useMemo, useState} from "react";
import {MapLine} from "../polyline/MapLine/MapLine";
import {RoutingPolyline, routingColorByIndex} from "../polyline/RoutingPolyline/RoutingPolyline";

type RoadRoute = SiteRoadRoute & {route: RouteResult};

export type UseSiteConnectionsResult = {
  connections: SiteConnection[];
  /** Routed units by `routeKey` of their position. */
  roadRoutes: Map<string, RoadRoute>;
};

const NO_ROUTES = new Map<string, RoadRoute>();

/**
 * The units a clicked site connects to, and road routes for the closest few when the display asks. Routes are
 * asked from the site out to each unit; drive distance and time are close enough either way for a glance.
 */
export const useSiteConnections = (
  site: SiteEntity | undefined,
  locations: LocationContext[],
  display: LiveMapDisplayOptions,
): UseSiteConnectionsResult => {
  const [triggerRoutes] = useLazyGetRoutingOriginManyResultQuery();
  const [roadRoutes, setRoadRoutes] = useState(NO_ROUTES);

  const point = site?.location ? {id: site.id, lat: site.location.latCoord, lng: site.location.longCoord} : undefined;
  const {siteLinks, siteLinkRadiusMiles, siteRoadRoutes} = display;
  const connections = useMemo(
    () => point ? findSiteConnections(point, locations, {siteLinks, siteLinkRadiusMiles}) : [],
    // The point is rebuilt each render; its parts are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [point?.id, point?.lat, point?.lng, locations, siteLinks, siteLinkRadiusMiles]
  );

  // Only re-route when the routed units or their positions (to about 100 m) change, not on every poll.
  const routed = siteRoadRoutes ? connections.slice(0, SITE_ROAD_ROUTE_LIMIT) : [];
  const routedKey = routed.map(({lat, lng}) => `${lat.toFixed(3)},${lng.toFixed(3)}`).join("|");

  useEffect(() => {
    if (!point || routed.length === 0) {
      setRoadRoutes(NO_ROUTES);
      return;
    }
    let current = true;
    triggerRoutes({
      origin: {lat: point.lat, lon: point.lng},
      destinations: routed.map(({lat, lng}) => ({lat, lon: lng})),
    })
      .unwrap()
      .then(response => {
        if (!current) return;
        const next = new Map<string, RoadRoute>();
        response.results.forEach((result, index) => {
          const unit = routed[index];
          if (!result.route || !unit) return;
          next.set(routeKey(unit.lat, unit.lng), {
            meters: result.route.totalDistanceMeters,
            millis: result.route.totalTimeMillis,
            route: result.route,
          });
        });
        setRoadRoutes(next);
      })
      .catch(() => current && setRoadRoutes(NO_ROUTES));
    return () => {
      current = false;
    };
    // routedKey stands in for the routed units and the site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routedKey, point?.id, triggerRoutes]);

  return {connections, roadRoutes};
};

// Dashes: Google draws them as repeated symbols over an invisible line.
const dashed = (color: string, opacity: number): Omit<google.maps.PolylineOptions, "map" | "path"> => ({
  strokeOpacity: 0,
  clickable: false,
  zIndex: 5,
  icons: [{
    icon: {path: "M 0,-1 0,1", strokeColor: color, strokeOpacity: opacity, strokeWeight: 2, scale: 3},
    offset: "0",
    repeat: "12px",
  }],
});

/** Headed-here lines are amber, nearest are white; units out of shift are fainter. */
const LINE_STYLES = {
  headed: {inShift: dashed("#f59e0b", 0.95), outOfShift: dashed("#f59e0b", 0.45)},
  nearest: {inShift: dashed("#ffffff", 0.9), outOfShift: dashed("#ffffff", 0.4)},
};

export type SiteConnectionLinesProps = {
  site: SiteEntity;
  display: LiveMapDisplayOptions;
  connections: SiteConnection[];
  roadRoutes: Map<string, RoadRoute>;
};

/** Lines from a clicked site to its connected units: along the road where routed, straight and dashed otherwise. */
export const SiteConnectionLines = ({site, display, connections, roadRoutes}: SiteConnectionLinesProps) => {
  const location = site.location;
  if (!location || display.siteLinks === "off") return null;
  const origin = {lat: location.latCoord, lng: location.longCoord};
  const styles = LINE_STYLES[display.siteLinks];
  return <>
    {connections.map((connection, index) => {
      const key = `${connection.ctx.entityType}::${connection.ctx.entityId}`;
      const road = roadRoutes.get(routeKey(connection.lat, connection.lng));
      return road
        ? <RoutingPolyline key={key} id={`site-route-${key}`} route={road.route} geodesic strokeColor={routingColorByIndex(index)}/>
        : <MapLine
          key={key}
          path={[origin, {lat: connection.lat, lng: connection.lng}]}
          options={connection.inShift ? styles.inShift : styles.outOfShift}/>;
    })}
  </>;
};
